import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import to from 'await-to-js';
import { toast } from 'react-toastify';
import { RootState } from './store';
import APIService from '../Utils/APIService';
import { DEFAULT_PAGE_SIZE } from '../Utils/constants';
import { showAddNewServiceModal, showEditServiceModal } from './app-ui.store';
import { getAllVehicles } from './vehicle.store';
import { ServiceRecordDTO, ServiceRecordPayload, ServiceRecordUpdatePayload } from '../Types/ServiceRecord';

export interface ServiceRecordState {
  serviceRecords: ServiceRecordDTO[];
  loading: boolean;
  error: boolean;
  selectedVehicleId: number | null;
}

const initialState: ServiceRecordState = {
  serviceRecords: [],
  loading: false,
  error: false,
  selectedVehicleId: null,
};

const refreshVehicles = () => getAllVehicles({ page: 0, page_size: DEFAULT_PAGE_SIZE });

export const getAllServiceRecords = createAsyncThunk<ServiceRecordDTO[], number>(
  'service-record/get-all',
  async (vehicleId, { rejectWithValue }) => {
    const [err, res] = await to(
      APIService.get('user/vehicle-service-record', {
        params: { vehicle_id: vehicleId, page: 1, page_size: 100 },
      }),
    );
    if (err) {
      toast.error('Something went wrong!. Could not fetch the service records');
      return rejectWithValue(err);
    }
    return res?.data?.data ?? [];
  },
);

export const addNewServiceRecord = createAsyncThunk<any, ServiceRecordPayload>(
  'service-record/add',
  async (payload, { rejectWithValue, dispatch }) => {
    const [err, res] = await to(APIService.post('user/vehicle-service-record', payload));
    if (err) {
      toast.error(`${err.message || 'Something went wrong!'}. Could not create the service record`);
      return rejectWithValue(err);
    }
    toast.success('Successfully added the service record');
    dispatch(showAddNewServiceModal(false));
    dispatch(refreshVehicles());
    dispatch(getAllServiceRecords(payload.vehicle_id));
    return res?.data;
  },
);

export const editServiceRecord = createAsyncThunk<
  any,
  { id: string; payload: ServiceRecordUpdatePayload; vehicleId: number }
>('service-record/edit', async ({ id, payload, vehicleId }, { rejectWithValue, dispatch }) => {
  const [err, res] = await to(APIService.put(`user/vehicle-service-record/${id}`, payload));
  if (err) {
    toast.error(`${err.message || 'Something went wrong!'}. Could not update the service record`);
    return rejectWithValue(err);
  }
  toast.success('Successfully updated the service record');
  dispatch(showEditServiceModal(null));
  dispatch(refreshVehicles());
  dispatch(getAllServiceRecords(vehicleId));
  return res?.data;
});

export const deleteServiceRecord = createAsyncThunk<any, { id: string; vehicleId: number }>(
  'service-record/delete',
  async ({ id, vehicleId }, { rejectWithValue, dispatch }) => {
    const [err, res] = await to(APIService.delete(`user/vehicle-service-record/${id}`));
    if (err) {
      toast.error(`${err.message || 'Something went wrong!'}. Could not delete the service record`);
      return rejectWithValue(err);
    }
    toast.success('Successfully deleted the service record');
    dispatch(refreshVehicles());
    dispatch(getAllServiceRecords(vehicleId));
    return res?.data;
  },
);

export const serviceRecordSlice = createSlice({
  name: 'serviceRecord',
  initialState,
  reducers: {
    setSelectedVehicleId: (state, { payload }: { payload: number | null }) => {
      state.selectedVehicleId = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllServiceRecords.pending, (store) => {
      store.loading = true;
      store.error = false;
    });
    builder.addCase(getAllServiceRecords.fulfilled, (store, { payload }) => {
      store.loading = false;
      store.serviceRecords = payload || [];
    });
    builder.addCase(getAllServiceRecords.rejected, (store) => {
      store.loading = false;
      store.error = true;
    });
  },
});

export const { setSelectedVehicleId } = serviceRecordSlice.actions;

export const serviceRecordStore = (store: RootState): ServiceRecordState => store.serviceRecord;
export const getServiceRecordsData = createSelector(serviceRecordStore, (s) => s.serviceRecords);
export const getServiceRecordsLoading = createSelector(serviceRecordStore, (s) => s.loading);
export const getSelectedVehicleId = createSelector(serviceRecordStore, (s) => s.selectedVehicleId);

export default serviceRecordSlice.reducer;
